/*
 * Description:
 * ---------------------
 * Wi-Fi Networks: the Wi-Fi networks this box can see and the one it is on,
 * and, for an operator, joining, leaving or forgetting one from afar. The
 * box's own setup page (cloud-anchor/pkg/setupui) does the same from a device
 * on its network; this screen asks the same routes through the proxy. A join
 * is started in the background and its outcome read from the status, since
 * every proxy hop gives up on a request long before a join ends; a box built
 * before that shows its networks here and is joined from its own page. All
 * wording lives in wifiWords.js.
 *
 * ! file components/WifiPanel.js
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiRefreshCw } from "react-icons/fi";
import useEdgeFeature, { FEATURE_ABSENT, FEATURE_UNREACHABLE } from "../hooks/useEdgeFeature";
import useEdgeWrite from "../hooks/useEdgeWrite";
import useVisibleInterval from "../hooks/useVisibleInterval";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import { useConfirm } from "./ConfirmModal";
import { useSession } from "./PortalGate";
import { IS_SINGLE_EDGE, nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import * as W from "./wifiWords";
import "./DeviceLimits.css";
import "./DataBudgets.css";
import "./Wifi.css";

/* A scan waits for the radio to sweep every channel, which takes the box
   several seconds on its own. */
const SCAN_TIMEOUT_MS = 20000;

async function getJSON(url, timeoutMs) {
  const res = await fetchWithTimeout(url, {}, timeoutMs).catch(() => null);
  return res?.ok ? res.json().catch(() => null) : null;
}

export default function WifiPanel({ selectedNode }) {
  const { state, payload: info, refresh } = useEdgeFeature(selectedNode, "/api/setup/info");
  useVisibleInterval(refresh, 15000, state === FEATURE_UNREACHABLE);
  if (!selectedNode) {
    return <div className="panel"><div className="panel-sub">Select a node to see its Wi-Fi.</div></div>;
  }
  if (state === FEATURE_ABSENT) {
    return <FeatureUnavailableNotice nodeId={selectedNode} feature="Wi-Fi from the console" />;
  }
  if (state === FEATURE_UNREACHABLE) {
    return (
      <div className="panel dl-warn">
        <div className="panel-title"><FiAlertTriangle size={17} /> Could not read Wi-Fi from {selectedNode}</div>
        <div className="panel-sub">The request did not come back. The screen keeps trying and fills in on its own.</div>
      </div>
    );
  }
  if (!info) return <div className="panel"><div className="panel-sub">Reading this box's Wi-Fi…</div></div>;
  if (!info.wifi_capable) return <div className="panel"><div className="panel-sub">{W.NO_MANAGER}</div></div>;
  // Keyed on the node, so one box's networks never show under another's name.
  return <Networks key={selectedNode} selectedNode={selectedNode} canJoin={info.background_join === true} />;
}

function Networks({ selectedNode, canJoin }) {
  const url = useMemo(() => nodeApiUrlFor(selectedNode), [selectedNode]);
  const { canWrite } = useSession();
  const confirm = useConfirm();
  const [status, setStatus] = useState(null);
  const [wans, setWans] = useState(null);
  const [lost, setLost] = useState(false);
  const [scan, setScan] = useState({ busy: false, networks: null, error: "" });
  const [captive, setCaptive] = useState(false);
  const [open, setOpen] = useState(""); // the network whose password form is open

  const read = useCallback(async () => {
    const [st, wn] = await Promise.all([getJSON(url("/api/setup/wifi/status")), getJSON(url("/wans"))]);
    setLost(!st);
    if (st) setStatus(st);
    if (wn) setWans(wn.wans || []);
  }, [url]);
  const { busy, error, send } = useEdgeWrite(selectedNode, read);
  const underWay = !!status?.join?.joining;
  useVisibleInterval(read, underWay ? 2000 : 8000);

  const rescan = useCallback(async () => {
    setScan((s) => ({ ...s, busy: true, error: "" }));
    const res = await fetchWithTimeout(url("/api/setup/wifi/scan"), {}, SCAN_TIMEOUT_MS).catch(() => null);
    const body = res ? await res.json().catch(() => null) : null;
    if (res?.ok && body) setScan({ busy: false, networks: body.networks || [], error: "" });
    else setScan((s) => ({ ...s, busy: false, error: body?.error || "The scan did not come back. Try again." }));
  }, [url]);
  const radio = !!status?.device || !!status?.connected;
  useEffect(() => {
    if (radio) rescan();
  }, [radio, rescan]);

  const checkCaptive = useCallback(async (force) => {
    const c = await getJSON(url(`/api/setup/wifi/captive${force ? "?force=1" : ""}`), 15000);
    setCaptive(!!(c?.captive && c.wifi_connected));
  }, [url]);
  const on = status?.connected ? status.ssid : "";
  useEffect(() => {
    setCaptive(false);
    if (on) checkCaptive(false);
  }, [on, checkCaptive]);

  if (!status) {
    return lost ? (
      <div className="panel dl-warn">
        <div className="panel-title"><FiAlertTriangle size={17} /> Could not read this box's Wi-Fi</div>
        <div className="panel-sub">The request did not come back. The screen keeps trying and fills in on its own.</div>
      </div>
    ) : <div className="panel"><div className="panel-sub">Reading this box's Wi-Fi…</div></div>;
  }

  const manage = canWrite && canJoin;
  const only = W.onlyLine(status, wans);
  const head = W.headline(status, wans, captive);
  const failed = W.failedJoin(status);
  const join = async (ssid, password, hidden = false) => {
    if (on && on !== ssid && !(await confirm(W.confirmSwitch(on, ssid, only, IS_SINGLE_EDGE)))) return;
    if (await send("POST", "/api/setup/wifi/connect", { ssid, password, hidden, background: true })) setOpen("");
  };
  const leave = async (ask, path, body) => {
    if (await confirm(ask)) await send("POST", path, body);
  };

  return (
    <div className="dl wifi">
      {error && <div className="dl-error" role="alert">{error}</div>}
      <div className={`panel db-headline wifi-${head.tone}`}>
        <div className="wifi-headline">
          <span role="status">{head.text}</span>
          {on && manage && !underWay && (
            <div className="dl-actions">
              <button type="button" className="dl-btn" disabled={busy}
                onClick={() => leave(W.confirmDisconnect(on, only, IS_SINGLE_EDGE), "/api/setup/wifi/disconnect")}>
                Disconnect
              </button>
              <button type="button" className="dl-btn danger" disabled={busy}
                onClick={() => leave(W.confirmForget(on, only, IS_SINGLE_EDGE), "/api/setup/wifi/forget", { ssid: on })}>
                Forget
              </button>
            </div>
          )}
        </div>
        {captive && (
          <p className="db-warning" role="note">
            <FiAlertTriangle size={15} /> <span>{W.CAPTIVE}</span>
            <button type="button" className="dl-btn" onClick={() => checkCaptive(true)}>Check again</button>
          </p>
        )}
        {on && (
          <details className="dl-measure">
            <summary>Show the details</summary>
            <span>Radio: {status.device}</span>
            {status.ipv4 && <span>The box's address on this network: {status.ipv4}</span>}
            {status.signal > 0 && <span>Signal: {status.signal}%</span>}
          </details>
        )}
      </div>
      {lost && <p className="dl-note">The box did not answer the last read, so this may be out of date. The screen keeps trying.</p>}
      {failed && <p className="db-warning" role="alert"><FiAlertTriangle size={15} /> <span>{failed}</span></p>}
      {radio && !canWrite && <p className="dl-note">{W.READ_ONLY}</p>}
      {radio && canWrite && !canJoin && (
        <FeatureUnavailableNotice nodeId={selectedNode} feature={W.NEEDS_UPDATE_FEATURE} detail={W.NEEDS_UPDATE} />
      )}
      {radio && (
        <div className="panel">
          <div className="dl-head">
            <div className="panel-title">Networks nearby</div>
            <button type="button" className="dl-btn" onClick={rescan} disabled={scan.busy}>
              <FiRefreshCw size={13} /> {scan.busy ? "Scanning…" : "Scan again"}
            </button>
          </div>
          {scan.error && <p className="dl-note">{scan.error}</p>}
          {!scan.networks && !scan.error && <p className="dl-note">Scanning the networks around the box…</p>}
          {scan.networks?.length === 0 && <p className="dl-note">No Wi-Fi networks in range.</p>}
          {scan.networks?.map((n) => (
            <Network key={n.ssid} n={n} current={n.ssid === on} manage={manage} disabled={busy || underWay}
              open={open === n.ssid} onOpen={() => setOpen(n.ssid)} onClose={() => setOpen("")}
              onJoin={(password) => join(n.ssid, password)} />
          ))}
        </div>
      )}
      {radio && manage && (
        <details className="panel wifi-hidden">
          <summary>Join a hidden network</summary>
          <JoinForm hidden busy={busy || underWay} onJoin={(password, ssid) => join(ssid, password, true)} />
        </details>
      )}
    </div>
  );
}

function Network({ n, current, manage, disabled, open, onOpen, onClose, onJoin }) {
  const locked = !W.isOpen(n.security);
  const company = W.isCompany(n.security);
  const about = [company ? "Company sign-in" : locked ? "Password" : "Open", W.band(n.freq_mhz), `${W.signalWord(n.signal)} signal`];
  return (
    <div>
      <div className="dl-row">
        <Bars pct={n.signal} />
        <div className="dl-row-main">
          <strong>{n.ssid}</strong>
          <span>{about.filter(Boolean).join(" · ")}</span>
        </div>
        {current && <span className="wifi-chip">Connected</span>}
        {!current && manage && company && <span className="dl-muted">Not supported</span>}
        {!current && manage && !company && !open && (
          <button type="button" className="dl-btn" disabled={disabled} aria-label={`Join ${n.ssid}`}
            onClick={locked ? onOpen : () => onJoin("")}>
            Join
          </button>
        )}
      </div>
      {open && <JoinForm ssid={n.ssid} busy={disabled} onJoin={onJoin} onCancel={onClose} />}
    </div>
  );
}

function JoinForm({ ssid, hidden = false, busy, onJoin, onCancel }) {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    onJoin(password, hidden ? name.trim() : ssid);
  };
  return (
    <form className="dl-form" onSubmit={submit} aria-label={hidden ? "Join a hidden network" : `Join ${ssid}`}>
      {hidden && (
        <label className="dl-field">
          <span>Network name</span>
          <input className="dl-input" value={name} onChange={(e) => setName(e.target.value)} required
            autoComplete="off" spellCheck={false} aria-label="Hidden network name" />
        </label>
      )}
      <label className="dl-field">
        <span>Password</span>
        <input className="dl-input" type={show ? "text" : "password"} value={password} required={!hidden}
          onChange={(e) => setPassword(e.target.value)} autoComplete="off" autoFocus={!hidden}
          aria-label={hidden ? "Hidden network password" : `Password for ${ssid}`} />
        <button type="button" className="dl-btn" onClick={() => setShow(!show)}>{show ? "Hide" : "Show"}</button>
      </label>
      <div className="dl-actions">
        <button type="submit" className="dl-btn primary" disabled={busy}>Join</button>
        {onCancel && <button type="button" className="dl-btn" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}

/* Four bars, lit the way a phone lights them; the row says the word. */
function Bars({ pct }) {
  const lit = W.bars(pct);
  return (
    <span className="wifi-bars" aria-hidden="true" title={`${pct}%`}>
      {[1, 2, 3, 4].map((i) => <i key={i} className={i <= lit ? "on" : ""} />)}
    </span>
  );
}
